import { useEffect, useMemo, useState } from "react";
import { Modal, Table, Button, Form, Spinner, OverlayTrigger, Tooltip, Alert } from "react-bootstrap";
import { EyeFill, EyeSlashFill, Trash } from "react-bootstrap-icons";
import http from "services/httpClient";

interface Recipient {
  id: number;
  device_id: number;
  column_index: number;
  user_id: number;
  alert_type: "email" | "sms" | "both" | null;
  enabled: boolean;
  alarm_cleared: boolean | null;
}

interface ClientUser {
  id: number;
  user_id?: number;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  user?: {
    id?: number;
    email?: string;
    first_name?: string | null;
    last_name?: string | null;
    role?: string | null;
  };
}

interface TriggerRecipientsModalProps {
  show: boolean;
  onClose: () => void;
  deviceId: number;
  columnIndex: number | null;
  triggerLabel: string;
  clientId: number;
  smsEnabled: boolean;
  onRecipientsChanged?: () => void;
}

const normalizeRecipients = (payload: any): Recipient[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.recipients)) return payload.recipients;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeUsers = (payload: any): ClientUser[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getUserId = (u: ClientUser): number => {
  return u.user?.id ?? u.user_id ?? u.id;
};

const getUserEmail = (u: ClientUser): string => {
  return u.user?.email ?? u.email ?? "";
};

const getUserFirstName = (u: ClientUser): string => {
  return u.user?.first_name ?? u.first_name ?? "";
};

const getUserLastName = (u: ClientUser): string => {
  return u.user?.last_name ?? u.last_name ?? "";
};

const formatUserLabel = (u: ClientUser) => {
  const fullName = `${getUserFirstName(u)} ${getUserLastName(u)}`.trim();
  const email = getUserEmail(u);
  if (fullName && email) return `${fullName} (${email})`;
  if (email) return email;
  return `User #${getUserId(u)}`;
};

const sortUsers = (users: ClientUser[]): ClientUser[] => {
  return [...users].sort((a, b) => {
    const aName = formatUserLabel(a).toLowerCase();
    const bName = formatUserLabel(b).toLowerCase();
    return aName.localeCompare(bName);
  });
};

const sortTriggerRecipients = (rows: Recipient[], users: ClientUser[]): Recipient[] => {
  const getRecipientLabel = (rec: Recipient) => {
    const user = users.find((u) => getUserId(u) === rec.user_id);
    return user ? formatUserLabel(user).toLowerCase() : `user #${rec.user_id}`;
  };

  return [...rows].sort((a, b) => {
    return getRecipientLabel(a).localeCompare(getRecipientLabel(b));
  });
};

export const TriggerRecipientsModal: React.FC<TriggerRecipientsModalProps> = ({
  show,
  onClose,
  deviceId,
  columnIndex,
  triggerLabel,
  clientId,
  smsEnabled,
  onRecipientsChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [actionError, setActionError] = useState<string | null>(null);

  const recipientUserIds = useMemo(() => {
    return new Set(recipients.map((r) => r.user_id));
  }, [recipients]);

  const availableUsers = useMemo(() => {
    return clientUsers.filter((u) => !recipientUserIds.has(getUserId(u)));
  }, [clientUsers, recipientUserIds]);

  const fetchData = async () => {
    if (!show || !deviceId || columnIndex == null) return;

    try {
      setLoading(true);
      setActionError(null);

      const [recipientsRes, usersRes] = await Promise.all([
        http.get(`/triggers/recipients?device_id=${deviceId}&column_index=${columnIndex}`),
        http.get(`/clients/${clientId}/users`),
      ]);

      const normalizedUsers = sortUsers(normalizeUsers(usersRes.data));
      const normalizedRecipients = sortTriggerRecipients(
        normalizeRecipients(recipientsRes.data),
        normalizedUsers
      );

      setClientUsers(normalizedUsers);
      setRecipients(normalizedRecipients);
    } catch (err) {
      console.error("TriggerRecipientsModal fetchData error:", err);
      setActionError("Failed to load recipients");
      setRecipients([]);
      setClientUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && deviceId && columnIndex != null) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, deviceId, columnIndex, clientId]);

  const refreshRecipientsOnly = async () => {
    if (!deviceId || columnIndex == null) return;

    const recipientsRes = await http.get(
      `/triggers/recipients?device_id=${deviceId}&column_index=${columnIndex}`
    );
    const normalizedRecipients = sortTriggerRecipients(
      normalizeRecipients(recipientsRes.data),
      clientUsers
    );
    setRecipients(normalizedRecipients);
    onRecipientsChanged?.();
  };

  const addRecipient = async () => {
    if (!deviceId || columnIndex == null || !selectedUserId) return;

    try {
      setActionError(null);

      await http.post(`/triggers/recipients`, {
        device_id: deviceId,
        column_index: columnIndex,
        user_id: Number(selectedUserId),
        alert_type: null,
        enabled: false,
      });

      setSelectedUserId("");
      await fetchData();
      onRecipientsChanged?.();
    } catch (err: any) {
      if (err.response?.status === 409) {
        setActionError("Recipient already exists for this field");
      } else {
        setActionError("Failed to add recipient");
      }
    }
  };

  const addAllUsers = async () => {
    try {
      await http.post("/triggers/recipients/bulk", {
        device_id: deviceId,
        column_index: columnIndex,
        user_ids: availableUsers.map((u) => getUserId(u)),
        alert_type: "both",
        enabled: true,
      });

      await fetchData();
      onRecipientsChanged?.();
    } catch (err) {
      console.error(err);
    }
  };

  const updateRecipient = async (
    recipientId: number,
    patch: Partial<Pick<Recipient, "alert_type" | "enabled">>
  ) => {
    try {
      setActionError(null);
      await http.patch(`/triggers/recipients/${recipientId}`, patch);
      await refreshRecipientsOnly();
    } catch (err) {
      setActionError("Failed to update recipient");
    }
  };

  const removeRecipient = async (recipientId: number) => {
    try {
      setActionError(null);
      await http.delete(`/triggers/recipients/${recipientId}`);
      await fetchData();
      onRecipientsChanged?.();
    } catch (err) {
      setActionError("Failed to remove recipient");
    }
  };

  const resolveUser = (userId: number) => {
    return clientUsers.find((u) => getUserId(u) === userId);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Recipients — {triggerLabel}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {actionError && <Alert variant="danger">{actionError}</Alert>}

            <div className="d-flex gap-2 mb-3">      
              <Form.Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">Select user...</option>
                {availableUsers.map((u) => (
                  <option key={getUserId(u)} value={getUserId(u)}>
                    {formatUserLabel(u)}
                  </option>
                ))}
              </Form.Select>

              <Button onClick={addRecipient} disabled={!selectedUserId}>
                Add Selected Recipient
              </Button>

              <Button
                onClick={addAllUsers}
                disabled={availableUsers.length === 0}
              >
                Add All
              </Button>
            </div>

            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th className={!smsEnabled ? "text-muted" : ""}>SMS</th>
                  <th>Subscribed</th>
                  <th>Remove</th>
                </tr>
              </thead>

              <tbody>
                {recipients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      No recipients assigned.
                    </td>
                  </tr>
                ) : (
                  recipients.map((rec) => {
                    const user = resolveUser(rec.user_id);
                    const isEmail = rec.alert_type === "email" || rec.alert_type === "both";
                    const isSMS = rec.alert_type === "sms" || rec.alert_type === "both";

                    return (
                      <tr key={rec.id}>
                        <td>{user ? formatUserLabel(user) : `User #${rec.user_id}`}</td>

                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={isEmail}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              const current = rec.alert_type;

                              let newType: Recipient["alert_type"];
                              if (checked) {
                                newType =
                                  current === "sms" || current === "both" ? "both" : "email";
                              } else {
                                newType = current === "both" ? "sms" : null;
                              }

                              await updateRecipient(rec.id, { alert_type: newType });
                            }}
                          />
                        </td>

                        <td className={!smsEnabled ? "text-muted" : ""}>
                          <Form.Check
                            type="checkbox"
                            disabled={!smsEnabled}
                            checked={isSMS}
                            onChange={async (e) => {
                              if (!smsEnabled) return;

                              const checked = e.target.checked;
                              const current = rec.alert_type;

                              let newType: Recipient["alert_type"];
                              if (checked) {
                                newType =
                                  current === "email" || current === "both" ? "both" : "sms";
                              } else {
                                newType = current === "both" ? "email" : null;
                              }

                              await updateRecipient(rec.id, { alert_type: newType });
                            }}
                          />
                        </td>

                        <td className="text-center">
                          <span
                            onClick={() => updateRecipient(rec.id, { enabled: !rec.enabled })}
                            style={{ cursor: "pointer" }}
                          >
                            {rec.enabled ? <EyeFill /> : <EyeSlashFill className="text-muted" />}
                          </span>
                        </td>

                        <td className="text-center">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Remove recipient</Tooltip>}>
                            <Button
                              variant="link"
                              className="p-0 text-danger"
                              onClick={() => removeRecipient(rec.id)}
                            >
                              <Trash />
                            </Button>
                          </OverlayTrigger>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TriggerRecipientsModal;